'use client'

import React, { useState, useEffect, useCallback, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { CheckCircle, XCircle, ChevronRight, Award, BookOpen, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { EduQuiz, QuizQuestion, QuizResultPayload } from '@/hooks/useEducationSession'

// ─── Design tokens ────────────────────────────────────────────────────────────
const S = {
    sage: '#4a7c5e',
    sageDark: '#2d5a3d',
    sageLight: '#e8f2ec',
    sageMid: '#b8d9c4',
    cream: '#faf7f2',
    creamDark: '#f0ebe0',
    navy: '#1a2740',
    slate: '#4a5568',
    white: '#ffffff',
    studyBlue: '#3b5bdb',
    gold: '#c9903a',
    error: '#ef4444',
    success: '#22c55e',
    fontDisplay: "'Playfair Display', Georgia, serif",
    fontBody: "'Source Serif 4', Georgia, serif",
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface QuizInterfaceProps {
    quiz: {
        quiz_id?: string
        category_id?: string
        questions: QuizQuestion[]
        generated_at?: string
    }
    isOpen: boolean
    onSubmit: (answers: Record<string, number>) => Promise<QuizResultPayload | any>
    onDismiss: () => void
    categoryName?: string
    lastQuizResult?: QuizResultPayload | null
}

type Phase = 'answering' | 'reviewing' | 'complete'

interface AnswerState {
    [questionId: string]: number  // selected option index
}

// ─── Option button ────────────────────────────────────────────────────────────

function OptionButton({
    index,
    text,
    selected,
    onClick,
}: {
    index: number
    text: string
    selected: boolean
    onClick: () => void
}) {
    const label = ['A', 'B', 'C', 'D'][index] ?? String(index + 1)

    const bg = selected ? S.studyBlue : S.white
    const border = selected ? S.studyBlue : S.creamDark
    const textColor = selected ? S.white : S.navy

    return (
        <motion.button
            onClick={onClick}
            style={{
                width: '100%', textAlign: 'left', cursor: 'pointer',
                padding: '14px 16px', borderRadius: '14px',
                border: `2px solid ${border}`,
                background: bg, color: textColor,
                fontFamily: S.fontBody, fontSize: '15px', lineHeight: 1.5,
                display: 'flex', alignItems: 'flex-start', gap: '12px',
                transition: 'border-color 0.15s, background 0.15s',
            }}
            whileHover={{ scale: 1.01, y: -1 }}
            whileTap={{ scale: 0.99 }}
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.06 }}
        >
            {/* Label bubble */}
            <div style={{
                width: '26px', height: '26px', borderRadius: '8px', flexShrink: 0,
                background: selected ? 'rgba(255,255,255,0.25)' : S.sageLight,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 800, fontSize: '12px',
                color: selected ? S.white : S.sage,
                marginTop: '1px',
            }}>
                {label}
            </div>
            <span style={{ flex: 1, paddingTop: '2px' }}>{text}</span>
        </motion.button>
    )
}

// ─── Score ring ───────────────────────────────────────────────────────────────

function ScoreRing({ score, total }: { score: number; total: number }) {
    const pct = total > 0 ? Math.round((score / total) * 100) : 0
    const r = 52
    const circ = 2 * Math.PI * r
    const offset = circ - (pct / 100) * circ
    const color = pct >= 80 ? S.gold : pct >= 60 ? S.sage : S.studyBlue

    return (
        <svg width="128" height="128" style={{ margin: '0 auto', display: 'block' }}>
            <circle cx="64" cy="64" r={r} fill="none" stroke={S.creamDark} strokeWidth="8" />
            <motion.circle
                cx="64" cy="64" r={r}
                fill="none" stroke={color} strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={circ}
                strokeDashoffset={offset}
                transform="rotate(-90 64 64)"
                initial={{ strokeDashoffset: circ }}
                animate={{ strokeDashoffset: offset }}
                transition={{ duration: 1.2, ease: 'easeOut', delay: 0.3 }}
            />
            <text x="64" y="60" textAnchor="middle" fill={S.navy} fontSize="28" fontWeight="800" fontFamily="system-ui">
                {score}
            </text>
            <text x="64" y="78" textAnchor="middle" fill={S.slate} fontSize="13" fontFamily="system-ui">
                / {total}
            </text>
        </svg>
    )
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function QuizInterface({
    quiz,
    isOpen,
    onSubmit,
    onDismiss,
    categoryName = 'Quiz',
    lastQuizResult,
}: QuizInterfaceProps) {
    const { t } = useTranslation()
    const [phase, setPhase] = useState<Phase>('answering')
    const [currentQ, setCurrentQ] = useState(0)
    const [answers, setAnswers] = useState<AnswerState>({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [serverResult, setServerResult] = useState<QuizResultPayload | null>(null)
    const [gradingError, setGradingError] = useState<string | null>(null)

    // ── Reset ONLY when a genuinely new quiz opens — never on lastQuizResult change.
    // If lastQuizResult were in deps, it would fire after grading completes and
    // bounce the student back to Question 1 (race condition + double-submit risk).
    const quizIdRef = useRef<string | null>(null)
    useEffect(() => {
        if (isOpen && quiz?.quiz_id !== quizIdRef.current) {
            quizIdRef.current = quiz?.quiz_id ?? null
            setPhase('answering')
            setCurrentQ(0)
            setAnswers({})
            setServerResult(null)   // fresh quiz — clear any stale result
            setGradingError(null)
        }
    }, [isOpen, quiz?.quiz_id])

    const questions = quiz?.questions || []
    const question: QuizQuestion | undefined = questions[currentQ]
    const totalQ = questions.length
    const selectedAnswer = question ? answers[question.id] : undefined
    const hasAnswered = selectedAnswer !== undefined

    const handleSelect = useCallback((optionIdx: number) => {
        if (!question) return
        setAnswers(a => ({ ...a, [question.id]: optionIdx }))
    }, [question])

    const handleNext = () => {
        if (currentQ < totalQ - 1) {
            setCurrentQ(q => q + 1)
        } else {
            setPhase('reviewing')
        }
    }

    const handleSubmitAll = async () => {
        setIsSubmitting(true)
        setGradingError(null)
        try {
            const res = await onSubmit(answers)
            if (res) {
                setServerResult(res)
            }
            setPhase('complete')
        } catch (err: unknown) {
            // Surface error in complete phase rather than silently showing 0%.
            // The student's answers were saved server-side — encourage a retry.
            const msg = err instanceof Error ? err.message : 'Grading failed'
            setGradingError(
                msg.includes('500') || msg.toLowerCase().includes('server')
                    ? 'Grading is taking a moment — your answers were saved. Please close and try again.'
                    : `Couldn't grade your quiz: ${msg}`
            )
            setPhase('complete')
        } finally {
            setIsSubmitting(false)
        }
    }

    const effectiveResult = serverResult || lastQuizResult
    const finalScore = effectiveResult?.score ?? 0
    const finalReview = effectiveResult?.review || []
    const totalQuestionsCount = finalReview.length > 0 ? finalReview.length : totalQ
    const passed = effectiveResult?.passed ?? (finalScore >= 70)
    const correctCount = finalReview.length > 0
        ? finalReview.filter(r => r.correct_yn).length
        : Math.round((finalScore / 100) * totalQuestionsCount)

    if (!isOpen) return null

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    style={{
                        position: 'fixed', inset: 0, zIndex: 100,
                        display: 'flex', flexDirection: 'column',
                        background: 'rgba(26,39,64,0.55)',
                        backdropFilter: 'blur(6px)',
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    {/* ── Modal card ── */}
                    <motion.div
                        style={{
                            margin: 'auto',
                            width: '100%', maxWidth: '580px',
                            maxHeight: '90dvh',
                            background: S.white, borderRadius: '24px',
                            boxShadow: '0 32px 100px rgba(26,39,64,0.3)',
                            display: 'flex', flexDirection: 'column',
                            overflow: 'hidden',
                        }}
                        initial={{ scale: 0.92, y: 32 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.94, y: 24, opacity: 0 }}
                        transition={{ type: 'spring', stiffness: 340, damping: 30 }}
                    >
                        {/* ── Header bar ── */}
                        <div style={{
                            background: `linear-gradient(135deg, ${S.navy} 0%, #243452 100%)`,
                            padding: '18px 24px',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <BookOpen size={18} style={{ color: 'rgba(255,255,255,0.8)' }} />
                                <span style={{ fontFamily: S.fontDisplay, fontSize: '16px', fontWeight: 700, color: S.white }}>
                                    {phase === 'complete'
                                        ? t('education.quiz.resultsTitle', 'Quiz Results')
                                        : t('education.quiz.title', { name: categoryName, defaultValue: `${categoryName} Quiz` })}
                                </span>
                            </div>

                            {/* Progress dots — answering phase */}
                            {phase === 'answering' && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    {questions.map((_, i) => (
                                        <motion.div
                                            key={i}
                                            style={{
                                                width: i === currentQ ? '18px' : '6px',
                                                height: '6px', borderRadius: '3px',
                                                background: i < currentQ
                                                    ? 'rgba(255,255,255,0.9)'
                                                    : i === currentQ
                                                        ? S.white
                                                        : 'rgba(255,255,255,0.3)',
                                                transition: 'all 0.25s',
                                            }}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* ── Body (scrollable) ── */}
                        <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>

                            {/* ─ ANSWERING PHASE ─ */}
                            {phase === 'answering' && (
                                !question ? (
                                    <div style={{ textAlign: 'center', padding: '40px 20px', background: S.cream, borderRadius: '20px' }}>
                                        <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }}>
                                            <p style={{ color: S.navy, fontWeight: 700, fontSize: '18px', marginBottom: '8px' }}>
                                                {t('education.quiz.error.noQuestions', 'No questions available')}
                                            </p>
                                            <p style={{ color: S.slate, fontSize: '14px', marginBottom: '24px' }}>
                                                {t('education.quiz.error.noQuestionsDesc', 'Unable to load quiz questions.')}
                                            </p>
                                            <button
                                                onClick={onDismiss}
                                                style={{
                                                    padding: '12px 24px', background: S.studyBlue, color: S.white,
                                                    border: 'none', borderRadius: '12px', fontWeight: 600, cursor: 'pointer'
                                                }}
                                            >
                                                {t('education.quiz.error.goBack', 'Close')}
                                            </button>
                                        </motion.div>
                                    </div>
                                ) : (
                                    <AnimatePresence mode="wait">
                                        <motion.div
                                            key={currentQ}
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -20 }}
                                            transition={{ duration: 0.18 }}
                                        >
                                            {/* Question counter */}
                                            <div style={{ fontSize: '12px', color: S.sage, fontWeight: 700, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                {t('education.quiz.questionOf', { n: currentQ + 1, total: totalQ, defaultValue: `Question ${currentQ + 1} of ${totalQ}` })}
                                                {question.difficulty === 'stretch' && (
                                                    <span style={{
                                                        marginLeft: '8px', background: S.gold, color: S.white,
                                                        padding: '1px 7px', borderRadius: '6px', fontSize: '10px',
                                                    }}>
                                                        ⭐ {t('education.quiz.stretch', 'Stretch')}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Question text */}
                                            <p style={{
                                                fontFamily: S.fontBody, fontSize: '17px', fontWeight: 600,
                                                color: S.navy, lineHeight: 1.6, marginBottom: '20px',
                                            }}>
                                                {question.question}
                                            </p>

                                            {/* Options */}
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                {Array.isArray(question?.options) ? question.options.map((opt, i) => (
                                                    <OptionButton
                                                        key={i}
                                                        index={i}
                                                        text={opt}
                                                        selected={selectedAnswer === i}
                                                        onClick={() => handleSelect(i)}
                                                    />
                                                )) : (
                                                    <div style={{ padding: '20px', textAlign: 'center', background: S.cream, borderRadius: '12px' }}>
                                                        <p style={{ color: S.slate }}>{t('education.subjectCard.loadingDetails', 'Loading...')}</p>
                                                    </div>
                                                )}
                                            </div>
                                        </motion.div>
                                    </AnimatePresence>
                                )
                            )}

                            {/* ─ REVIEWING PHASE (Before final submission) ─ */}
                            {phase === 'reviewing' && (
                                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                                    <h3 style={{ fontFamily: S.fontDisplay, fontSize: '18px', fontWeight: 700, color: S.navy, marginBottom: '8px' }}>
                                        {t('education.quiz.reviewAnswers', 'Review Your Answers')}
                                    </h3>
                                    <p style={{ fontSize: '14px', color: S.slate, marginBottom: '16px' }}>
                                        Double check your answers before submitting for grading.
                                    </p>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                        {questions.map((q, qi) => {
                                            const userAns = answers[q.id]
                                            const hasChosen = userAns !== undefined
                                            return (
                                                <div
                                                    key={q.id}
                                                    onClick={() => {
                                                        setCurrentQ(qi)
                                                        setPhase('answering')
                                                    }}
                                                    style={{
                                                        padding: '14px 16px', borderRadius: '14px',
                                                        border: `1px solid ${hasChosen ? S.sageMid : S.creamDark}`,
                                                        background: hasChosen ? S.sageLight : S.cream,
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                                        <p style={{ fontSize: '14px', fontWeight: 600, color: S.navy, margin: 0, lineHeight: 1.4 }}>
                                                            {qi + 1}. {q.question}
                                                        </p>
                                                        <span style={{ fontSize: '12px', color: S.studyBlue, fontWeight: 600, marginLeft: '8px', whiteSpace: 'nowrap' }}>
                                                            Edit
                                                        </span>
                                                    </div>
                                                    <p style={{ fontSize: '13px', color: hasChosen ? S.sageDark : S.slate, margin: 0, fontWeight: 500 }}>
                                                        {hasChosen ? `✓ Selected: ${q.options[userAns]}` : '⚠️ Not answered'}
                                                    </p>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </motion.div>
                            )}

                            {/* ─ COMPLETE PHASE (After server grading) ─ */}
                            {phase === 'complete' && (
                                <motion.div
                                    style={{ textAlign: 'center', padding: '16px 0' }}
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                >
                                    {/* Error banner — only shows on server failure */}
                                    {gradingError && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            style={{
                                                background: '#fef2f2',
                                                border: `1.5px solid ${S.error}40`,
                                                borderRadius: '12px',
                                                padding: '14px 18px',
                                                marginBottom: '20px',
                                                textAlign: 'left',
                                            }}
                                        >
                                            <p style={{ fontSize: '14px', color: '#dc2626', fontWeight: 600, margin: 0 }}>
                                                ⚠️ {gradingError}
                                            </p>
                                        </motion.div>
                                    )}

                                    {/* Only render score ring when we have real results */}
                                    {!gradingError && (
                                        <ScoreRing score={correctCount} total={totalQuestionsCount} />
                                    )}

                                    <motion.div
                                        initial={{ opacity: 0, y: 12 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.3 }}
                                    >
                                        <h3 style={{ fontFamily: S.fontDisplay, fontSize: '22px', fontWeight: 700, color: S.navy, margin: '20px 0 8px' }}>
                                            {passed
                                                ? t('education.quiz.wellDone', 'Well Done!')
                                                : t('education.quiz.keepGoing', 'Keep Practicing!')}
                                        </h3>
                                        <p style={{ fontSize: '15px', color: S.slate, lineHeight: 1.6, marginBottom: '12px' }}>
                                            {effectiveResult?.message || (passed
                                                ? t('education.quiz.wellDoneMsg', 'You passed the quiz! Keep up the great work.')
                                                : t('education.quiz.keepGoingMsg', 'Review the concepts below and give it another shot!'))}
                                        </p>
                                        <div style={{
                                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                                            background: passed ? S.sageLight : '#fee2e2',
                                            borderRadius: '10px', padding: '6px 14px', marginBottom: '20px',
                                        }}>
                                            <Award size={14} style={{ color: passed ? S.sage : S.error }} />
                                            <span style={{ fontSize: '13px', fontWeight: 700, color: passed ? S.sage : S.error }}>
                                                {finalScore}% {t('education.quiz.score', 'Score')}
                                            </span>
                                        </div>
                                    </motion.div>

                                    {/* Detailed Review Breakdown from Server */}
                                    {finalReview.length > 0 && (
                                        <div style={{ textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '10px' }}>
                                            <h4 style={{ fontFamily: S.fontDisplay, fontSize: '16px', fontWeight: 700, color: S.navy, margin: '0 0 4px' }}>
                                                Question Breakdown
                                            </h4>
                                            {finalReview.map((item, idx) => (
                                                <div
                                                    key={idx}
                                                    style={{
                                                        padding: '14px 16px', borderRadius: '14px',
                                                        border: `1.5px solid ${item.correct_yn ? S.success : S.error}`,
                                                        background: item.correct_yn ? '#f0fdf4' : '#fef2f2',
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
                                                        {item.correct_yn
                                                            ? <CheckCircle size={16} style={{ color: S.success, flexShrink: 0, marginTop: '2px' }} />
                                                            : <XCircle size={16} style={{ color: S.error, flexShrink: 0, marginTop: '2px' }} />}
                                                        <p style={{ fontSize: '14px', fontWeight: 600, color: S.navy, margin: 0, lineHeight: 1.4 }}>
                                                            {idx + 1}. {item.question}
                                                        </p>
                                                    </div>
                                                    <p style={{ fontSize: '13px', color: item.correct_yn ? '#166534' : '#991b1b', margin: '0 0 4px 24px', fontWeight: 500 }}>
                                                        Your answer: {item.student_answer}
                                                    </p>
                                                    {!item.correct_yn && (
                                                        <p style={{ fontSize: '13px', color: '#166534', margin: '0 0 6px 24px', fontWeight: 600 }}>
                                                            ✓ Correct answer: {item.correct}
                                                        </p>
                                                    )}
                                                    {item.explanation && (
                                                        <p style={{ fontSize: '12px', color: S.slate, margin: '4px 0 0 24px', lineHeight: 1.4, fontStyle: 'italic' }}>
                                                            💡 {item.explanation}
                                                        </p>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </motion.div>
                            )}
                        </div>

                        {/* ── Footer ── */}
                        <div style={{
                            padding: '16px 24px',
                            borderTop: `1px solid ${S.creamDark}`,
                            display: 'flex', gap: '10px',
                        }}>
                            {phase === 'answering' && (
                                <button
                                    onClick={handleNext}
                                    disabled={!hasAnswered}
                                    style={{
                                        flex: 1, padding: '13px', borderRadius: '13px',
                                        border: 'none',
                                        background: hasAnswered ? S.sage : S.creamDark,
                                        color: hasAnswered ? S.white : S.slate,
                                        fontFamily: S.fontBody, fontSize: '15px', fontWeight: 700,
                                        cursor: hasAnswered ? 'pointer' : 'not-allowed',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                                        transition: 'all 0.15s',
                                    }}
                                >
                                    {currentQ < totalQ - 1
                                        ? t('education.quiz.nextQuestion', 'Next Question')
                                        : t('education.quiz.reviewAnswers', 'Review Answers')}
                                    <ChevronRight size={16} />
                                </button>
                            )}

                            {phase === 'reviewing' && (
                                <button
                                    onClick={handleSubmitAll}
                                    disabled={isSubmitting}
                                    style={{
                                        flex: 1, padding: '13px', borderRadius: '13px',
                                        border: 'none', background: S.sage, color: S.white,
                                        fontFamily: S.fontBody, fontSize: '15px', fontWeight: 700,
                                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                        opacity: isSubmitting ? 0.7 : 1,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                    }}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 size={16} className="animate-spin" />
                                            {t('education.quiz.submitting', 'Submitting for grading...')}
                                        </>
                                    ) : (
                                        t('education.quiz.submitQuiz', 'Submit Quiz')
                                    )}
                                </button>
                            )}

                            {phase === 'complete' && (
                                <button
                                    onClick={onDismiss}
                                    style={{
                                        flex: 1, padding: '13px', borderRadius: '13px',
                                        border: 'none',
                                        background: `linear-gradient(135deg, ${S.sage}, ${S.sageDark})`,
                                        color: S.white,
                                        fontFamily: S.fontBody, fontSize: '15px', fontWeight: 700,
                                        cursor: 'pointer',
                                        boxShadow: '0 4px 16px rgba(74,124,94,0.3)',
                                    }}
                                >
                                    {t('education.quiz.continueLearning', 'Continue Learning')}
                                </button>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}